/**
 * Pure, framework-agnostic engine that answers product-listing queries from an
 * already-fetched Algolia dataset — entirely in memory, with zero network calls.
 *
 * The PLP preloads the full result set for a category ONCE (server-side, Redis-cached),
 * then every filter / sort / facet-count operation is computed here. This mirrors the
 * relevant subset of Algolia's query semantics so the existing InstantSearch-shaped UI
 * can be driven from local data:
 *   - facetFilters         (AND across attributes, OR within an attribute)
 *   - numericFilters       (range refinements with <,<=,>,>=,=,!=)
 *   - disjunctive faceting (each facet's option counts ignore that facet's own selection)
 *   - facet stats          (min/max/avg/sum for numeric facets)
 *
 * This is the typed engine for the SERVER-PRELOADED (Redis-cached) path only. Its sibling
 * `algolia/cachedProductsSearchClient.ts` carries an equivalent engine for the browser-driven
 * surfaces, which fetch Algolia directly. The two are intentionally NOT shared: this one reads the
 * slimmed cache shape (see backend AlgoliaApi.slimForCache), that one reads raw Algolia records, so
 * merging them would re-couple the two payload shapes.
 */

export type AnyRecord = Record<string, any>;

/** Where the preloaded dataset came from: Redis cache vs a fresh Algolia fetch. */
export interface DatasetCacheInfo {
  /** True when every underlying query was served from the Redis cache. */
  cached: boolean;
  /** Number of underlying queries served from Redis. */
  cachedCount: number;
  /** Number of underlying queries fetched fresh from Algolia. */
  algoliaCount: number;
  /** 'cache' when fully cached, 'algolia' when any query was a cache miss. */
  source: 'cache' | 'algolia';
}

/**
 * Everything the browser needs to fetch the hits that were withheld from the SSR payload. Attached by
 * `buildPlpDataset` whenever the result set is larger than the initial batch; consumed by
 * `LocalDatasetProvider`, which POSTs it to `/[locale]/api/plp-dataset` on mount.
 *
 * It is a verbatim echo of the query the server already ran, so the top-up hits the SAME Redis entry
 * (24h TTL) the SSR fetch populated — the remainder costs a cache read, not an Algolia query.
 */
export interface DatasetTopUp {
  /** Products index the SSR fetch used (AU/NZ base). Re-validated server-side against that pair. */
  indexName: string;
  /** The Algolia `filters` string — the CMS block's `algolia_filters_query`, `${localeKey}` resolved. */
  filters: string;
  /** Configured facet attributes — part of `attributesToRetrieve`, so the top-up hits match the SSR hits. */
  facetAttributes: string[];
  /** How many hits already shipped in the SSR payload; the route returns `hits.slice(offset)`. */
  offset: number;
}

export interface LocalDataset {
  /**
   * The hits present on the client RIGHT NOW. On first render this is only the initial SSR batch
   * (see INITIAL_SSR_HITS) — NOT the whole result set — and `LocalDatasetProvider` concatenates the
   * remainder onto it once the top-up XHR resolves. Read `totalHits` for the full size and
   * `isComplete` for whether the two are the same thing yet.
   */
  hits: AnyRecord[];
  /**
   * Facet attribute names — the CMS / Frontastic Studio facet keys the in-memory engine counts, in
   * their configured order (which is also the order the facet panel renders). Nothing facet-related
   * is read from the Algolia response on this path, so this configuration is the only source: no
   * discovered facet names, and no Algolia `renderingContent.facetOrdering` reordering the panel.
   */
  facetNames: string[];
  /**
   * Size of the FULL result set, which is what the listing counts, pagination controls and "load
   * more" affordances must be driven by. Differs from `hits.length` between first paint and the
   * top-up landing. Absent on datasets that were never split (e.g. `loadError`), where `hits.length`
   * is already the total.
   */
  totalHits?: number;
  /** Present when hits were withheld from the SSR payload — the query to re-run for the remainder. */
  topUp?: DatasetTopUp;
  /** Whether this dataset came from Redis cache or a fresh Algolia fetch (logged in the browser console). */
  cacheInfo?: DatasetCacheInfo;
  /**
   * Set when the server-side preload failed (backend/Algolia unreachable even after retries). The
   * listing section renders a "please retry" message instead of falling back to a client-side Algolia
   * query — so failures are visible and we never bill Algolia for browser-side listing queries.
   */
  loadError?: boolean;
  /**
   * Diagnostics logged to the BROWSER console (see cms-components-v2/section-with-product-listing) on
   * EVERY listing — on success and
   * on failure/0-products alike: the exact Algolia query sent, the full request JSON, and how many
   * products came back. Always present, so a query issue is visible client-side on any environment
   * without having to reproduce it with a special URL. It rides along in the SSR payload (~1 KB).
   */
  debug?: {
    /** Products returned (0 on failure or empty result). */
    nbProducts: number;
    /** Present when the fetch failed after backend retries (Algolia error or 0 products). */
    error?: string;
    /**
     * Every multi-query the SSR fetch POSTed to the `algoliaSearch/query` action, verbatim — one entry
     * per page pulled. On the failure path this is the page-0 request it would have sent.
     *
     * This is the ONLY copy of the index name and the `filters` string in the dataset — neither is
     * repeated as its own `debug` field. Worth knowing when reading the SSR HTML: React "outlines" any
     * long string into its own flight row and references it as `$<hexId>`, so a big listing's filter
     * shows up as e.g. `"filters":"$13"` rather than inline. Keeping one copy means one such row
     * instead of two (see .claude/scratch/capture.js, which resolves them).
     */
    request?: Array<{ indexName: string; params: Record<string, any> }>;
    /**
     * What the backend actually passed to Algolia's `client.search` — the cache MISSES only, so an
     * empty array means the whole dataset came from Redis and Algolia was never called. Absent when
     * running against a backend that predates the echo.
     */
    algoliaRequests?: Array<{ indexName: string; params: Record<string, any> }>;
  };
}

export type SortMode = 'default' | 'name_asc' | 'name_desc';

export interface LocalQueryParams {
  /** Disjunctive (OR-within, AND-across) selections, keyed by attribute. */
  refinementList?: Record<string, string[]>;
  /** Numeric range selections, keyed by attribute: [min, max] (inclusive). */
  numericRefinements?: Record<string, [number, number]>;
  /** 'default' keeps the as-fetched order (new first); name_asc/desc sort by localized name. */
  sortMode?: SortMode;
  /** Which facets to compute. '*' (or omitted) => every configured facetName. */
  facets?: string[] | '*';
  maxValuesPerFacet?: number;
}

/** A facet option count map: { value: count }. */
export type FacetData = Record<string, number>;

export interface LocalResults {
  hits: AnyRecord[];
  nbHits: number;
  page: number;
  nbPages: number;
  hitsPerPage: number;
  /** Conjunctive facet counts (over the fully filtered set). */
  facets: Record<string, FacetData>;
  facets_stats?: Record<string, { min: number; max: number; avg: number; sum: number }>;
  /**
   * Disjunctive facet counts — for each facet, computed over hits filtered by every OTHER
   * facet's refinements (but NOT this facet's). This is what InstantSearch's refinementList /
   * range connectors read, so multi-select keeps showing sibling options after one is picked.
   */
  disjunctiveFacets: Array<{
    name: string;
    data: FacetData;
    stats?: { min: number; max: number; avg: number; sum: number };
  }>;
  exhaustiveFacetsCount: boolean;
  exhaustiveNbHits: boolean;
  query: string;
  params: string;
  index: string;
  processingTimeMS: number;
  __isArtificial?: boolean;
}

// ---------------------------------------------------------------------------
// Attribute value extraction (descends through arrays + dotted paths). Paths are the canonical
// `attributes.*` spelling — see helpers/algolia/attribute-paths.ts.
// ---------------------------------------------------------------------------

const collectValues = (node: any, parts: string[], index: number, out: any[]) => {
  if (node === null || node === undefined) return;
  if (Array.isArray(node)) {
    for (const item of node) collectValues(item, parts, index, out);
    return;
  }
  if (index === parts.length) {
    out.push(node);
    return;
  }
  if (typeof node !== 'object') return;
  collectValues(node[parts[index]], parts, index + 1, out);
};

export const valuesAt = (record: AnyRecord, attribute: string): any[] => {
  const out: any[] = [];
  collectValues(record, attribute.split('.'), 0, out);
  return out;
};

// ---------------------------------------------------------------------------
// Disjunctive facet matching (OR within an attribute, AND across attributes)
// ---------------------------------------------------------------------------

/** Does `record` match the selected values for a single attribute (OR semantics)? */
const matchesAttributeSelection = (record: AnyRecord, attribute: string, selected: string[]): boolean => {
  if (!selected || selected.length === 0) return true;
  const values = valuesAt(record, attribute).map((v) => String(v));
  return selected.some((sel) => values.includes(String(sel)));
};

/** Does `record` satisfy every attribute's selection except those in `exclude`? */
const matchesRefinementList = (
  record: AnyRecord,
  refinementList: Record<string, string[]>,
  exclude?: string,
): boolean => {
  for (const attribute of Object.keys(refinementList)) {
    if (attribute === exclude) continue;
    if (!matchesAttributeSelection(record, attribute, refinementList[attribute])) return false;
  }
  return true;
};

// ---------------------------------------------------------------------------
// Numeric range matching ([min, max] inclusive)
// ---------------------------------------------------------------------------

const matchesNumericRange = (record: AnyRecord, attribute: string, range: [number, number]): boolean => {
  const [min, max] = range;
  const values = valuesAt(record, attribute)
    .map((v) => (typeof v === 'number' ? v : Number(v)))
    .filter((v) => Number.isFinite(v));
  if (values.length === 0) return false;
  return values.some((v) => v >= min && v <= max);
};

const matchesNumericRefinements = (
  record: AnyRecord,
  numericRefinements: Record<string, [number, number]>,
  exclude?: string,
): boolean => {
  for (const attribute of Object.keys(numericRefinements)) {
    if (attribute === exclude) continue;
    const range = numericRefinements[attribute];
    if (!range) continue;
    const [min, max] = range;
    // An "open" range (-Infinity..Infinity / [-1,1] reset) is treated as no filter.
    if (!Number.isFinite(min) && !Number.isFinite(max)) continue;
    if (!matchesNumericRange(record, attribute, range)) return false;
  }
  return true;
};

// ---------------------------------------------------------------------------
// Facet counting
// ---------------------------------------------------------------------------

const countFacet = (hits: AnyRecord[], attribute: string, maxValuesPerFacet: number) => {
  const counts = new Map<string, number>();
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let numericCount = 0;
  let allNumeric = true;

  for (const hit of hits) {
    const seen = new Set<string>();
    for (const value of valuesAt(hit, attribute)) {
      const key = String(value);
      // Skip blank values — Algolia's own facet engine omits these (avoids empty filter options).
      if (key.trim() === '') continue;
      if (seen.has(key)) continue;
      seen.add(key);
      counts.set(key, (counts.get(key) || 0) + 1);
      const numeric = typeof value === 'number' ? value : typeof value === 'boolean' ? NaN : Number(value);
      if (Number.isFinite(numeric)) {
        min = Math.min(min, numeric);
        max = Math.max(max, numeric);
        sum += numeric;
        numericCount += 1;
      } else {
        allNumeric = false;
      }
    }
  }

  const sorted = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, maxValuesPerFacet);

  return {
    data: Object.fromEntries(sorted) as FacetData,
    size: counts.size,
    stats: allNumeric && numericCount > 0 ? { min, max, avg: sum / numericCount, sum } : undefined,
  };
};

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/** `name` may be a localized object (e.g. { 'en-AU': '...' }) or a string. */
export const sortableName = (hit: AnyRecord): string => {
  const name = hit?.name;
  if (typeof name === 'string') return name;
  if (name && typeof name === 'object') {
    const first = Object.values(name).find((value) => typeof value === 'string');
    if (first) return first as string;
  }
  return '';
};

const applySort = (hits: AnyRecord[], sortMode: SortMode): AnyRecord[] => {
  if (sortMode === 'default') return hits; // keep the as-fetched order (new first)
  const sorted = [...hits].sort((a, b) => {
    const order = sortableName(a).localeCompare(sortableName(b), undefined, { numeric: true, sensitivity: 'base' });
    return sortMode === 'name_asc' ? order : -order;
  });
  return sorted;
};

// ---------------------------------------------------------------------------
// Main query
// ---------------------------------------------------------------------------

const normalizeFacets = (facets: string[] | '*' | undefined, facetNames: string[]): string[] => {
  if (!facets || facets === '*') return facetNames;
  if (Array.isArray(facets)) return facets.includes('*') ? facetNames : facets;
  return [facets];
};

/**
 * Run a full in-memory query over the dataset. Returns ALL matching hits (no pagination —
 * the PLP renders every tile), the conjunctive facet counts, and the disjunctive facet
 * counts that the refinementList / range connectors consume.
 */
export const queryLocalDataset = (dataset: LocalDataset, params: LocalQueryParams = {}): LocalResults => {
  const refinementList = params.refinementList ?? {};
  const numericRefinements = params.numericRefinements ?? {};
  const sortMode = params.sortMode ?? 'default';
  const maxValuesPerFacet = params.maxValuesPerFacet && params.maxValuesPerFacet > 0 ? params.maxValuesPerFacet : 999;
  const facetNames = normalizeFacets(params.facets, dataset.facetNames);

  // Fully filtered hits (every refinement applied) — these are the products shown.
  const filtered = dataset.hits.filter(
    (hit) => matchesRefinementList(hit, refinementList) && matchesNumericRefinements(hit, numericRefinements),
  );
  const hits = applySort(filtered, sortMode);

  // Conjunctive facets — counts over the fully filtered set.
  const facets: Record<string, FacetData> = {};
  const facetsStats: Record<string, { min: number; max: number; avg: number; sum: number }> = {};

  // Disjunctive facets — for each facet, re-filter ignoring that facet's own refinement.
  const disjunctiveFacets: LocalResults['disjunctiveFacets'] = [];

  for (const name of facetNames) {
    const conj = countFacet(filtered, name, maxValuesPerFacet);
    if (conj.size > 0) {
      facets[name] = conj.data;
      if (conj.stats) facetsStats[name] = conj.stats;
    }

    const disjunctiveHits = dataset.hits.filter(
      (hit) =>
        matchesRefinementList(hit, refinementList, name) && matchesNumericRefinements(hit, numericRefinements, name),
    );
    const disj = countFacet(disjunctiveHits, name, maxValuesPerFacet);
    if (disj.size > 0) {
      disjunctiveFacets.push({ name, data: disj.data, stats: disj.stats });
    }
  }

  return {
    hits,
    nbHits: hits.length,
    page: 0,
    nbPages: 1,
    hitsPerPage: hits.length,
    facets,
    facets_stats: Object.keys(facetsStats).length > 0 ? facetsStats : undefined,
    disjunctiveFacets,
    exhaustiveFacetsCount: true,
    exhaustiveNbHits: true,
    query: '',
    params: '',
    index: '',
    processingTimeMS: 0,
    __isArtificial: false,
  };
};
